import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Search,
  AlertTriangle,
  RefreshCw,
  PackageSearch,
  Route as RouteIcon,
  Info,
  Save,
  Loader2,
  CheckCircle2,
  ArrowRight,
  Table2,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button, buttonVariants } from '@/components/ui/button';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { PageHeader } from '@/components/patterns';
import { Skeleton } from '@/components/ui/skeleton';
import { AppLayout } from '@/components/layout/app-layout';
import { useToast } from '@/lib/hooks/use-toast';
import { tradeService } from '../services/trade-service';
import { formatFullDate } from '../utils/trade-dates';
import { customerService } from '@/features/customers/services/customer-service';
import { RouteSearchBar, OWN_IMPORT, type RouteSearch } from '../components/route-search-bar';
import { NuevoClienteDialog } from '../components/nuevo-cliente-dialog';
import { QuoteComparison } from '../components/quote-comparison';
import { QuoteOptionDetail } from '../components/quote-option-detail';
import type { TradeQuoteOptions, QuoteResult, SavedQuote } from '../types/trade.types';

/**
 * Cotizador de flete de importación.
 *
 * Responde UNA pregunta: ¿cuánto cuesta traer este embarque? Cuatro campos —
 * origen, destino, equipo y peso — porque cotizar el flete no necesita saber
 * qué productos son.
 *
 * El costo por producto (partida arancelaria, tributos, costo nacionalizado) se
 * calcula **después**, en la pestaña Costos de la orden, con la proforma ya
 * cargada. Son dos preguntas distintas del negocio y mezclarlas en una pantalla
 * fue justamente lo que la hacía confusa.
 *
 * Forma: búsqueda de rutas, no formulario. Barra arriba, opciones lado a lado
 * para poder compararlas, y el desglose de la elegida abajo.
 */

const EMPTY_SEARCH: RouteSearch = {
  originLocationId: '',
  destinationLocationId: '',
  containerTypeId: '',
  weightKg: '',
  customerId: OWN_IMPORT,
};

export function CotizadorPage() {
  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        <CotizadorContent />
      </div>
    </AppLayout>
  );
}

/** El encabezado de Comercia (artefacto «Rutas»): la pregunta y, si se sabe, el estado del tarifario. */
function EncabezadoDelCotizador({ salud }: { salud?: { vigentes: number; vencidas: number } }) {
  return (
    <PageHeader
      modulo="comercia"
      etiqueta="Comercia · Logística"
      icono={RouteIcon}
      titulo="¿Cuánto cuesta traerlo?"
      subtitulo="Compará rutas y transportistas con el tarifario vigente."
      acciones={
        salud && (
          <Link
            to="/trade/tarifario"
            className="inline-flex items-center gap-2 rounded-full border bg-card px-3.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <Table2 className="h-4 w-4" aria-hidden="true" />
            <span className="tabular-nums">{salud.vigentes}</span> tarifas vigentes
            {salud.vencidas > 0 && (
              <span className="font-semibold text-alerta">· {salud.vencidas} vencidas</span>
            )}
          </Link>
        )
      }
    />
  );
}

/**
 * Lo que viene precargado en la URL, al llegar de recotizar o duplicar.
 *
 * Va por query string y no por el estado de la navegación para que el enlace se
 * pueda compartir o volver a abrir: quien recarga la página no pierde lo que
 * estaba cotizando.
 *
 * Solo se toman los campos que llegan. Un parámetro ausente deja el default en
 * pie en vez de vaciar el select, que es lo que pasaría con un `??` ingenuo.
 */
function prefillFromUrl(params: URLSearchParams): Partial<RouteSearch> {
  const prefill: Partial<RouteSearch> = {};
  const origen = params.get('origen');
  const destino = params.get('destino');
  const equipo = params.get('equipo');
  const peso = params.get('peso');
  const cliente = params.get('cliente');

  if (origen) prefill.originLocationId = origen;
  if (destino) prefill.destinationLocationId = destino;
  if (equipo) prefill.containerTypeId = equipo;
  if (peso) prefill.weightKg = peso;
  // El cliente sí admite vacío: es "importación propia", una elección real.
  if (cliente !== null) prefill.customerId = cliente;

  return prefill;
}

function CotizadorContent() {
  const { toast } = useToast();
  const [params] = useSearchParams();

  const [catalogs, setCatalogs] = useState<TradeQuoteOptions | null>(null);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [search, setSearch] = useState<RouteSearch>(EMPTY_SEARCH);
  const [result, setResult] = useState<QuoteResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [mostrarNuevoCliente, setMostrarNuevoCliente] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<SavedQuote | null>(null);

  // ── Catálogos ──
  const loadCatalogs = useCallback(async () => {
    setLoadingCatalogs(true);
    setCatalogError(null);
    try {
      const [data, clientes] = await Promise.all([
        tradeService.getQuoteOptions(),
        // Si falla, se puede cotizar igual como importación propia.
        customerService.getCustomers().catch(() => []),
      ]);
      setCatalogs(data);
      setCustomers(clientes.map((c) => ({ id: c.id, name: c.name })));
      setSearch((prev) => ({
        ...prev,
        // Un select con una sola opción es una pregunta que no hace falta hacer.
        containerTypeId:
          data.containerTypes.length === 1 ? data.containerTypes[0].id : prev.containerTypeId,
        destinationLocationId:
          data.destinations.length === 1 ? data.destinations[0].id : prev.destinationLocationId,
        // Lo que viene de recotizar o duplicar manda sobre cualquier default:
        // es una elección explícita de quien llegó hasta acá.
        ...prefillFromUrl(params),
      }));
    } catch (error) {
      setCatalogError(
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'No se pudo cargar el tarifario.'
      );
    } finally {
      setLoadingCatalogs(false);
    }
  }, [params]);

  useEffect(() => {
    void loadCatalogs();
  }, [loadCatalogs]);

  // ── Buscar ──
  const handleSearch = useCallback(async () => {
    setSearching(true);
    setSelectedId(null);
    setSaved(null);
    try {
      const parsedWeight = Number(search.weightKg);
      const data = await tradeService.quote({
        originLocationId: search.originLocationId,
        destinationLocationId: search.destinationLocationId,
        containerTypeId: search.containerTypeId || null,
        weightKg: Number.isFinite(parsedWeight) && parsedWeight > 0 ? parsedWeight : null,
      });
      setResult(data);
      // La más económica queda elegida: es la respuesta por defecto.
      setSelectedId(data.options[0]?.id ?? null);
    } catch (error) {
      toast({
        title: 'No se pudo cotizar',
        description:
          (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Ocurrió un error al consultar el tarifario.',
        variant: 'destructive',
      });
    } finally {
      setSearching(false);
    }
  }, [search, toast]);

  // ── Guardar ──
  const handleSave = useCallback(async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const parsedWeight = Number(search.weightKg);
      const result = await tradeService.saveQuote({
        originLocationId: search.originLocationId,
        destinationLocationId: search.destinationLocationId,
        containerTypeId: search.containerTypeId || null,
        weightKg: Number.isFinite(parsedWeight) && parsedWeight > 0 ? parsedWeight : null,
        selectedOptionId: selectedId,
        // Cadena vacía = importación propia. El backend lo interpreta como null.
        customerId: search.customerId || null,
        // De dónde viene, si se llegó acá por recotizar o duplicar. Deja la
        // cadena a la vista: una ruta recotizada cuatro veces antes de cerrarse
        // dice algo que cuatro cotizaciones sueltas no dicen.
        quotedFromId: params.get('desde'),
      });
      setSaved(result);
      toast({
        title: `Cotización ${result.orderNumber} guardada`,
        description: 'Ya aparece en Cotizaciones y en el tablero de embarques.',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'No se pudo guardar',
        description:
          (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Ocurrió un error al guardar la cotización.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [selectedId, search, params, toast]);

  const [orden, setOrden] = useState<'precio' | 'transito'>('precio');
  const opcionesOrdenadas = useMemo(() => {
    const lista = [...(result?.options ?? [])];
    if (orden === 'transito') {
      // Sin días de tránsito, al final: no se puede decir que sea rápida.
      lista.sort(
        (a, b) =>
          (a.transitDaysMax ?? Number.POSITIVE_INFINITY) -
            (b.transitDaysMax ?? Number.POSITIVE_INFINITY) || a.totalPrice - b.totalPrice
      );
    } else {
      lista.sort((a, b) => a.totalPrice - b.totalPrice);
    }
    return lista;
  }, [result, orden]);

  const selectedOption = useMemo(
    () => result?.options.find((o) => o.id === selectedId) ?? null,
    [result, selectedId]
  );

  // ── Render ──
  if (loadingCatalogs) {
    return (
      <>
        <EncabezadoDelCotizador />
        <Skeleton className="h-32 w-full rounded-tarjeta" />
        <Skeleton className="h-56 w-full rounded-tarjeta" />
      </>
    );
  }

  if (catalogError) {
    return (
      <>
        <EncabezadoDelCotizador />
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <AlertTriangle className="h-8 w-8 text-alerta" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">{catalogError}</p>
            <Button size="sm" variant="outline" onClick={() => void loadCatalogs()}>
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Reintentar
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  if (!catalogs || catalogs.origins.length === 0) {
    return (
      <>
        <EncabezadoDelCotizador />
        <EmptyTariffState />
      </>
    );
  }

  const { expiredRates, totalRates } = catalogs.tariffHealth;

  return (
    <>
      <EncabezadoDelCotizador
        salud={{ vigentes: totalRates - expiredRates, vencidas: expiredRates }}
      />

      {/* ── Buscador ── */}
      <section
        aria-label="Buscar rutas"
        className="rounded-tarjeta border bg-card p-4 shadow-sm sm:p-5"
      >
        <RouteSearchBar
          origins={catalogs.origins}
          destinations={catalogs.destinations}
          containerTypes={catalogs.containerTypes}
          customers={customers}
          value={search}
          onChange={setSearch}
          onSearch={() => void handleSearch()}
          searching={searching}
          onNuevoCliente={() => setMostrarNuevoCliente(true)}
        />
      </section>

      {/*
        El cliente recién creado se agrega a la lista y queda elegido, para no
        obligar a buscarlo de nuevo apenas se creó.
      */}
      <NuevoClienteDialog
        open={mostrarNuevoCliente}
        onOpenChange={setMostrarNuevoCliente}
        onCreado={(cliente) => {
          setCustomers((prev) =>
            [...prev, cliente].sort((a, b) => a.name.localeCompare(b.name, 'es'))
          );
          setSearch((s) => ({ ...s, customerId: cliente.id }));
        }}
      />

      {/* El aviso del artefacto: una franja suave con la salida a la mano. */}
      {expiredRates > 0 && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-alerta-borde bg-alerta-suave px-4 py-3 text-sm text-foreground">
          <AlertTriangle className="h-4 w-4 shrink-0 text-alerta" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="font-semibold">{expiredRates} tarifas están vencidas.</span> Se puede
            cotizar igual, pero confirmá con el agente antes de prometer el precio.
          </span>
          <Link to="/trade/tarifario" className="font-semibold text-alerta underline">
            Actualizar tarifario
          </Link>
        </p>
      )}

      {/* ── Resultados ── */}
      {!result && <IdleState />}

      {result && result.options.length === 0 && <NoRoutesState warnings={result.warnings} />}

      {result && result.options.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">
                {result.options.length}{' '}
                {result.options.length === 1 ? 'ruta disponible' : 'rutas disponibles'}
                <span className="text-sm font-normal text-muted-foreground">
                  {' '}
                  · {result.meta.originName} → {result.meta.destinationName}
                  {result.meta.containerTypeCode && ` · ${result.meta.containerTypeCode}`}
                </span>
              </h2>
              <p className="text-xs text-muted-foreground">
                {result.meta.ratesConsidered} tarifas evaluadas · elegí una para ver su desglose
              </p>
            </div>
            {result.options.length > 1 && (
              <GrupoDeOpciones
                aria-label="Ordenar las rutas"
                valor={orden}
                onCambiar={setOrden}
                opciones={[
                  { valor: 'precio', texto: 'Más barata' },
                  { valor: 'transito', texto: 'Más rápida' },
                ]}
              />
            )}
          </div>

          {result.warnings.length > 0 && (
            <div className="space-y-1 rounded-xl border border-alerta-borde bg-alerta-suave px-4 py-2.5">
              {result.warnings.map((w, i) => (
                <p
                  key={i}
                  className="flex items-start gap-1.5 text-sm leading-snug text-foreground"
                >
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  {w}
                </p>
              ))}
            </div>
          )}

          <QuoteComparison
            options={opcionesOrdenadas}
            selectedId={selectedId}
            onSelect={setSelectedId}
            detalle={selectedOption && <QuoteOptionDetail option={selectedOption} />}
          />

          {selectedOption && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-tarjeta border bg-card p-4 shadow-sm">
              {saved ? (
                <>
                  <p className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-ok" aria-hidden="true" />
                    <span>
                      <span className="font-semibold">{saved.orderNumber}</span> guardada ·{' '}
                      <span className="text-muted-foreground">
                        {saved.isOwnImport ? 'importación propia' : 'para el cliente'} · vence el{' '}
                        {formatFullDate(saved.validUntil)}
                      </span>
                    </span>
                  </p>
                  <Link
                    to={`/trade/cotizaciones/${saved.id}`}
                    className={buttonVariants({ variant: 'outline' })}
                  >
                    Abrir la cotización
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Al guardarla aparece en <strong>Cotizaciones</strong> y en la primera columna
                    del tablero de embarques, con sus tramos ya cargados.
                  </p>
                  <Button
                    variant="degradado"
                    className="px-5"
                    onClick={() => void handleSave()}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        Generando…
                      </>
                    ) : (
                      <>
                        <Save className="mr-1.5 h-4 w-4" />
                        Generar cotización
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          )}

          <p className="flex items-start gap-1.5 rounded-xl bg-muted/50 px-4 py-2.5 text-xs leading-snug text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Este es el <strong className="font-semibold">flete del embarque</strong>. El costo de
            cada producto puesto en Bolivia —con gravamen, IVA y costo nacionalizado— se calcula en
            la orden, una vez cargada la proforma del proveedor.
          </p>
        </div>
      )}
    </>
  );
}

// ── Estados vacíos ───────────────────────────────────────────────────────────

/**
 * Lo primero que ve una empresa recién creada.
 *
 * Antes decía "el tarifario está vacío" y ahí terminaba: el usuario nuevo se
 * queda mirando una pantalla que le informa un problema sin darle la salida. El
 * botón es la diferencia entre un mensaje de error y un primer paso.
 *
 * Y explica **por qué** está vacío: que cada empresa cargue el suyo no es una
 * limitación, es que un tarifario son precios negociados y mostrarle a una
 * empresa los de otra sería filtrar su negociación.
 */
function EmptyTariffState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <PackageSearch className="h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-medium">Todavía no hay tarifas cargadas</p>
        <p className="max-w-md text-xs leading-relaxed text-muted-foreground">
          Sin tarifas no hay rutas que ofrecer. Subí el Excel del tarifario y en un minuto se puede
          cotizar.
          <br />
          <br />
          Cada empresa carga el suyo: un tarifario son precios negociados con las navieras, y lo que
          consiguió una no es lo que consiguió la otra.
        </p>
        <Link
          to="/trade/tarifario"
          className={buttonVariants({ variant: 'degradado', className: 'mt-1 px-5' })}
        >
          <Table2 className="h-4 w-4" aria-hidden="true" />
          Ir a cargar el tarifario
        </Link>
      </CardContent>
    </Card>
  );
}

function IdleState() {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
        <Search className="h-9 w-9 text-muted-foreground" />
        <p className="text-sm font-medium">Elegí origen y destino, y buscá</p>
        <p className="max-w-md text-xs text-muted-foreground">
          Vas a ver las rutas disponibles una al lado de la otra, con los días de tránsito, el
          precio y de dónde sale cada tarifa.
        </p>
      </CardContent>
    </Card>
  );
}

function NoRoutesState({ warnings }: { warnings: string[] }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-2 py-14 text-center">
        <PackageSearch className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">No hay tarifas para esa combinación</p>
        {warnings.map((w, i) => (
          <p key={i} className="max-w-md text-xs text-muted-foreground">
            {w}
          </p>
        ))}
      </CardContent>
    </Card>
  );
}
